/* Sykell comparator v2 — no dependencies, lazy data fetch, accessible rendering.
   Comparatif INTERNE (décision marchand 31/08/2026) : le vélo courant + jusqu'à 3 autres
   modèles Sykell de la MÊME GAMME (liste « comparer_avec » du JSON, repli = même famille
   d'usage puis prix les plus proches — règle gamme du 01/09/2026), chaque colonne
   liant vers sa fiche produit. Ligne finale « Idéal pour » = positionnement de chaque modèle.
   Prix et images des modèles suggérés récupérés en direct via /products/<handle>.js
   (jamais de prix figé dans le JSON) ; en cas d'échec, repli sur le JSON puis pictogramme.
   Pas de repli d'affichage : handle inconnu ou aucun modèle à suggérer => section masquée. */
(() => {
  'use strict';
  const features = [
    ['autonomie','Autonomie (max)'],['vitesse_max','Vitesse max'],['moteur','Moteur'],['batterie','Batterie'],['pneus','Pneus'],['freins','Freins'],['transmission','Transmission'],['charge_max','Charge max'],['poids','Poids'],['prix','Prix']
  ];
  const esc = value => String(value ?? '—').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[c]));
  const BIKE_ICON = '<svg viewBox="0 0 64 40" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="13" cy="28" r="8.5"/><circle cx="51" cy="28" r="8.5"/><path d="M13 28 L24 12 L44 12 L51 28 M24 12 L32 28 L44 12 M13 28 L32 28"/><path d="M20 8 L28 8 M42 8 L47 12"/></svg>';
  const bikeImage = b => b.image_fond_blanc
    ? `<img class="sykell-comparator__bike-image" src="${esc(b.image_fond_blanc)}" alt="${esc((b.marque || '') + ' ' + (b.nom || ''))}" loading="lazy" width="64" height="50">`
    : `<span class="sykell-comparator__bike-image sykell-comparator__bike-icon">${BIKE_ICON}</span>`;
  const formatPrice = cents => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
  // Données vivantes de la boutique (prix courant, image, URL localisée) pour un modèle suggéré.
  const enrich = async bike => {
    try {
      const response = await fetch(`/products/${bike.handle}.js`, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const p = await response.json();
      if (typeof p.price === 'number') {
        bike.caracteristiques = Object.assign({}, bike.caracteristiques, { prix: formatPrice(p.price) });
      }
      if (p.featured_image) {
        bike.image_fond_blanc = p.featured_image + (p.featured_image.includes('?') ? '&' : '?') + 'width=320';
      }
      if (p.url) { bike.url = p.url; }
    } catch (error) {
      // Repli silencieux : prix du JSON s'il existe, pictogramme, lien construit sur le handle.
      if (!bike.caracteristiques?.prix && typeof bike.valeurs?.prix === 'number') {
        bike.caracteristiques = Object.assign({}, bike.caracteristiques, { prix: formatPrice(bike.valeurs.prix * 100) });
      }
    }
    if (!bike.url) { bike.url = `/products/${bike.handle}`; }
    return bike;
  };
  const init = async root => {
    const status = root.querySelector('[data-status]'), target = root.querySelector('[data-table-wrap]');
    try {
      const response = await fetch(root.dataset.dataUrl, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const bikes = data.velos_sykell;
      const base = bikes.find(b => b.handle === root.dataset.productHandle);
      if (!base) { root.hidden = true; return; }
      const current = Object.assign({}, base, {
        // Nom court du JSON (« GS9 ») plutôt que le titre SEO complet de la fiche.
        nom: base.nom || root.dataset.productTitle,
        marque: 'Sykell',
        image_fond_blanc: root.dataset.productImage || null,
        // Prix courant reformaté en fr-FR (comme les modèles suggérés) plutôt que le format
        // brut du filtre money de Shopify (« €2.390,00 ») — cohérence de la ligne Prix.
        caracteristiques: Object.assign({}, base.caracteristiques,
          root.dataset.productPriceValue ? { prix: formatPrice(Math.round(parseFloat(root.dataset.productPriceValue) * 100)) }
          : root.dataset.productPrice ? { prix: root.dataset.productPrice } : {}),
        valeurs: Object.assign({}, base.valeurs, root.dataset.productPriceValue ? { prix: parseFloat(root.dataset.productPriceValue) } : {})
      });
      const wanted = (base.comparer_avec || []).map(h => bikes.find(b => b.handle === h)).filter(Boolean);
      // Repli si `comparer_avec` est vide ou pointe des handles disparus : on reste dans la
      // gamme (famille d'usage) du vélo courant, et à défaut on prend les prix les plus proches
      // — jamais un modèle d'une autre famille sans nécessité. Cf. règle gamme du JSON (v2.1).
      const others = bikes.filter(b => b.handle !== base.handle);
      const sameGamme = others.filter(b => b.gamme && base.gamme && b.gamme === base.gamme);
      const basePrice = base.valeurs?.prix;
      const byPrice = list => typeof basePrice === 'number'
        ? list.slice().sort((a, b) => Math.abs((a.valeurs?.prix ?? Infinity) - basePrice) - Math.abs((b.valeurs?.prix ?? Infinity) - basePrice))
        : list;
      const fallback = byPrice(sameGamme.length ? sameGamme : others);
      const siblings = (wanted.length ? wanted : fallback).slice(0, 3).map(b => Object.assign({}, b));
      if (!siblings.length) { root.hidden = true; return; }
      await Promise.all(siblings.map(enrich));
      const all = [current, ...siblings];
      const head = all.map((b, i) => `<th scope="col" class="${i === 0 ? 'is-sykell' : ''}"><div class="sykell-comparator__bike-head">${bikeImage(b)}<span class="sykell-comparator__bike-name">${esc(b.nom)}</span><span class="sykell-comparator__bike-brand">${esc(b.marque)}</span>${i === 0 ? '' : `<a class="sykell-comparator__bike-cta" href="${esc(b.url)}">Voir ce modèle</a>`}</div></th>`).join('');
      const specRows = features.map(([key, label]) => {
        const values = all.map(b => b.caracteristiques?.[key] || '—');
        // Une ligne renseignée sur moins de 2 vélos n'aide pas à comparer : on la masque.
        if (values.filter(v => v !== '—').length < 2) { return ''; }
        return `<tr><th scope="row">${label}</th>${values.map((v, i) => `<td class="${i === 0 ? 'is-sykell' : ''}">${esc(v)}</td>`).join('')}</tr>`;
      }).join('');
      const idealRow = all.some(b => b.ideal_pour)
        ? `<tr class="sykell-comparator__ideal-row"><th scope="row">Idéal pour</th>${all.map((b, i) => `<td class="${i === 0 ? 'is-sykell' : ''}">${esc(b.ideal_pour || '—')}</td>`).join('')}</tr>`
        : '';
      target.innerHTML = `<table class="sykell-comparator__table"><caption class="visually-hidden">Comparatif de ${esc(current.nom)} avec d'autres modèles Sykell</caption><thead><tr><th scope="col">Caractéristique</th>${head}</tr></thead><tbody>${specRows}${idealRow}</tbody></table>`;
      const h2 = root.querySelector('.sykell-comparator__intro h2');
      if (h2 && base.nom) { h2.textContent = `${base.nom} ou un autre Sykell ?`; }
      if (status) { status.remove(); }
    } catch (error) { if (status) { status.textContent = 'Le comparatif est momentanément indisponible.'; } console.error('[Sykell comparator]', error); }
  };
  document.querySelectorAll('.sykell-comparator').forEach(init);
})();
